import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { getCheckoutStatus } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useLang } from '../lib/lang';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, AlertCircle, ArrowRight, Trophy, Coins, Crown, Ticket, Sparkles, User as UserIcon, Home } from 'lucide-react';
import { motion } from 'framer-motion';

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { lang } = useLang();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ loading: true, payment_status: null, type: null, amount: null, error: null, fulfilled: false, details: null });
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, error: 'Session manquante' });
      return;
    }
    let cancelled = false;
    let attempt = 0;
    const MAX_ATTEMPTS = 10;
    const POLL_INTERVAL = 1500;

    const poll = async () => {
      try {
        const res = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        const d = res.data;
        setState({
          loading: false,
          payment_status: d.payment_status,
          type: d.type,
          amount: d.amount_gbp,
          fulfilled: d.fulfilled,
          details: d.details || null,
        });
        if (d.payment_status === 'paid' && d.fulfilled) {
          if (refreshUser) refreshUser();
          return;
        }
        attempt += 1;
        setPollCount(attempt);
        if (attempt < MAX_ATTEMPTS) {
          setTimeout(poll, POLL_INTERVAL);
        }
      } catch (e) {
        if (cancelled) return;
        setState({ loading: false, error: e?.response?.data?.detail || 'Erreur' });
      }
    };
    poll();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const isPaid = state.payment_status === 'paid' && state.fulfilled;
  const isPending = !isPaid && !state.error;
  const details = state.details || {};

  // Localised messages per type/lang
  const isFR = lang === 'fr';

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-rolex-green via-[#0E4936] to-[#08291F] relative overflow-hidden">
      {/* Decorative gold spotlight */}
      <div className="absolute inset-0 hero-spotlight opacity-50 pointer-events-none" />
      <div className="absolute inset-0 noise-overlay opacity-40 pointer-events-none" />

      <main className="flex-1 flex items-center justify-center px-4 py-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-2xl"
        >
          <Card className="bg-cream border-2 border-gold/40 shadow-[0_30px_80px_rgba(0,0,0,0.5)] overflow-hidden">
            <CardContent className="p-0">

              {/* ─── LOADING STATE ─────────────────────── */}
              {(state.loading || isPending) && !state.error && (
                <div className="p-12 text-center">
                  <div className="w-20 h-20 rounded-full bg-gold-gradient flex items-center justify-center mx-auto mb-6 shadow-xl">
                    <Loader2 className="w-10 h-10 animate-spin text-rolex-green" />
                  </div>
                  <h1 className="font-display text-2xl font-bold text-rolex-green mb-3">
                    {isFR ? 'Confirmation du paiement…' : 'Confirming payment…'}
                  </h1>
                  <p className="text-rolex-green/65">
                    {isFR ? 'Nous validons votre transaction avec Stripe.' : 'We are validating your transaction with Stripe.'}
                  </p>
                  {pollCount > 0 && (
                    <p className="text-xs text-rolex-green/45 mt-3 font-mono">
                      {isFR ? `Tentative ${pollCount}/10` : `Attempt ${pollCount}/10`}
                    </p>
                  )}
                </div>
              )}

              {/* ─── ERROR STATE ─────────────────────── */}
              {state.error && (
                <div className="p-12 text-center">
                  <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                    <AlertCircle className="w-10 h-10 text-red-600" />
                  </div>
                  <h1 className="font-display text-2xl font-bold text-rolex-green mb-3">
                    {isFR ? 'Erreur' : 'Error'}
                  </h1>
                  <p className="text-rolex-green/65 mb-6">{state.error}</p>
                  <Button onClick={() => navigate('/')} className="btn-gold">
                    <Home className="w-4 h-4" /> {isFR ? "Retour à l'accueil" : 'Back home'}
                  </Button>
                </div>
              )}

              {/* ─── PAID STATE — type-specific celebrations ─────────────────────── */}
              {isPaid && state.type === 'raffle_direct' && (
                <RaffleSuccess details={details} amount={state.amount} isFR={isFR} navigate={navigate} />
              )}
              {isPaid && state.type === 'point_pack' && (
                <PointPackSuccess details={details} amount={state.amount} isFR={isFR} navigate={navigate} />
              )}
              {isPaid && state.type === 'vip' && (
                <VipSuccess details={details} amount={state.amount} isFR={isFR} navigate={navigate} />
              )}
              {isPaid && !['raffle_direct', 'point_pack', 'vip'].includes(state.type) && (
                <GenericSuccess amount={state.amount} isFR={isFR} navigate={navigate} />
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   RAFFLE DIRECT PURCHASE — primary success scenario
   ════════════════════════════════════════════════════════ */
function RaffleSuccess({ details, amount, isFR, navigate }) {
  const title = details.raffle_title || (isFR ? 'la compétition' : 'the competition');
  const quantity = details.quantity || 1;
  const value = Number(details.product_value || 0).toLocaleString();

  return (
    <>
      {/* Hero gold band with trophy */}
      <div className="relative bg-gradient-to-br from-[#D4AF37] via-[#E5C97A] to-[#A07828] py-12 px-8 text-center overflow-hidden">
        <div className="absolute inset-0 noise-overlay opacity-20 pointer-events-none" />
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 0.8, ease: 'backOut', delay: 0.2 }}
          className="w-24 h-24 rounded-full bg-rolex-green flex items-center justify-center mx-auto mb-5 shadow-[0_8px_32px_rgba(0,0,0,0.4)] border-4 border-cream/30"
        >
          <Trophy className="w-12 h-12 text-gold" strokeWidth={2.5} />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <p className="text-[10px] text-rolex-green/80 uppercase tracking-[0.3em] font-bold mb-2">
            {isFR ? '✓ Paiement confirmé' : '✓ Payment confirmed'}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-rolex-green leading-tight">
            {isFR ? 'Félicitations !' : 'Congratulations!'}
          </h1>
        </motion.div>
      </div>

      <div className="px-8 py-8 text-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <p className="text-rolex-green/75 text-lg leading-relaxed mb-5">
            {isFR ? 'Vous êtes officiellement entré dans la compétition' : 'You have officially entered the competition'}
          </p>

          {/* Raffle image + title block */}
          <div className="bg-rolex-green text-cream rounded-2xl p-6 mb-6 shadow-lg border border-gold/30 relative overflow-hidden">
            <div className="absolute inset-0 hero-spotlight opacity-30 pointer-events-none" />
            {details.raffle_image && (
              <img
                src={details.raffle_image.startsWith('http') ? details.raffle_image : details.raffle_image}
                alt={title}
                className="w-32 h-32 object-cover rounded-xl mx-auto mb-4 border-2 border-gold/40 shadow-xl relative z-10"
              />
            )}
            <p className="relative z-10 text-[10px] uppercase tracking-[0.25em] text-gold font-bold mb-2">
              <Sparkles className="w-3 h-3 inline mr-1" /> {isFR ? 'La Compétition' : 'The Competition'}
            </p>
            <h2 className="relative z-10 font-display text-2xl sm:text-3xl font-extrabold text-cream leading-tight mb-2">
              {title}
            </h2>
            {details.product_value > 0 && (
              <p className="relative z-10 font-mono text-lg text-gold">
                {isFR ? "D'une valeur de" : 'Worth'} <span className="font-bold">£{value}</span>
              </p>
            )}
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-4 mb-7">
            <div className="bg-cream border border-gold/20 rounded-xl p-4">
              <Ticket className="w-5 h-5 text-gold-dark mx-auto mb-2" />
              <p className="font-mono text-2xl font-bold text-rolex-green">{quantity}</p>
              <p className="text-[10px] uppercase tracking-wider text-rolex-green/60 mt-1">
                {isFR ? `Ticket${quantity > 1 ? 's' : ''}` : `Ticket${quantity > 1 ? 's' : ''}`}
              </p>
            </div>
            <div className="bg-gold-gradient/15 border-2 border-gold/40 rounded-xl p-4 gold-glow">
              <Coins className="w-5 h-5 text-gold-dark mx-auto mb-2" />
              <p className="font-mono text-2xl font-bold text-gold-dark">£{Number(amount).toFixed(2)}</p>
              <p className="text-[10px] uppercase tracking-wider text-rolex-green/60 mt-1">
                {isFR ? 'Payé' : 'Paid'}
              </p>
            </div>
          </div>

          <p className="text-sm text-rolex-green/65 mb-7 italic">
            🍀 {isFR ? 'Bonne chance pour le tirage !' : 'Good luck in the draw!'}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/account')} className="btn-gold gap-2 px-6 h-12 font-bold tracking-wide" data-testid="success-go-to-account">
              <UserIcon className="w-4 h-4" />
              {isFR ? 'Voir mon compte' : 'View my account'}
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" onClick={() => navigate('/')} className="border-rolex-green/30 text-rolex-green hover:bg-rolex-green/5 gap-2 h-12">
              <Home className="w-4 h-4" />
              {isFR ? 'Accueil' : 'Home'}
            </Button>
          </div>
        </motion.div>
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════════════
   POINT PACK SUCCESS
   ════════════════════════════════════════════════════════ */
function PointPackSuccess({ details, amount, isFR, navigate }) {
  const points = details.points_credited || 0;
  const packName = details.pack_name || (isFR ? 'Pack de points' : 'Point pack');

  return (
    <>
      <div className="relative bg-gradient-to-br from-[#D4AF37] via-[#E5C97A] to-[#A07828] py-12 px-8 text-center overflow-hidden">
        <div className="absolute inset-0 noise-overlay opacity-20 pointer-events-none" />
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 0.8, ease: 'backOut', delay: 0.2 }}
          className="w-24 h-24 rounded-full bg-rolex-green flex items-center justify-center mx-auto mb-5 shadow-2xl border-4 border-cream/30"
        >
          <Coins className="w-12 h-12 text-gold" strokeWidth={2.5} />
        </motion.div>
        <p className="text-[10px] text-rolex-green/80 uppercase tracking-[0.3em] font-bold mb-2">
          {isFR ? '✓ Paiement confirmé' : '✓ Payment confirmed'}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-rolex-green">
          {isFR ? 'Points crédités !' : 'Points credited!'}
        </h1>
      </div>

      <div className="px-8 py-8 text-center">
        <p className="text-rolex-green/75 text-lg leading-relaxed mb-5">
          {isFR ? 'Votre achat de' : 'Your purchase of'} <strong>{packName}</strong> {isFR ? 'a bien été enregistré.' : 'has been registered.'}
        </p>

        <div className="bg-rolex-green text-cream rounded-2xl p-8 mb-7 shadow-lg border border-gold/30 relative overflow-hidden">
          <div className="absolute inset-0 hero-spotlight opacity-30 pointer-events-none" />
          <p className="relative z-10 text-[10px] uppercase tracking-[0.25em] text-gold font-bold mb-3">
            <Sparkles className="w-3 h-3 inline mr-1" /> {isFR ? 'Points Crédités' : 'Points Credited'}
          </p>
          <p className="relative z-10 font-mono text-6xl font-black text-gold-gradient leading-none mb-2">
            +{points.toLocaleString()}
          </p>
          <p className="relative z-10 text-cream/70 text-sm uppercase tracking-wider">
            {isFR ? 'points' : 'points'}
          </p>
          <p className="relative z-10 text-cream/55 text-xs mt-4 font-mono">
            {isFR ? 'Payé' : 'Paid'}: £{Number(amount).toFixed(2)}
          </p>
        </div>

        <p className="text-sm text-rolex-green/65 mb-7">
          {isFR ? 'Vos points sont disponibles immédiatement pour participer aux tirages.' : 'Your points are available now to enter draws.'}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={() => navigate('/account')} className="btn-gold gap-2 px-6 h-12 font-bold tracking-wide">
            <UserIcon className="w-4 h-4" /> {isFR ? 'Voir mon compte' : 'View my account'} <ArrowRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={() => navigate('/')} className="border-rolex-green/30 text-rolex-green hover:bg-rolex-green/5 gap-2 h-12">
            <Home className="w-4 h-4" /> {isFR ? 'Accueil' : 'Home'}
          </Button>
        </div>
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════════════
   VIP SUBSCRIPTION SUCCESS
   ════════════════════════════════════════════════════════ */
function VipSuccess({ details, amount, isFR, navigate }) {
  const planName = details.plan_name || 'VIP';
  const ppm = details.points_per_month || 0;

  return (
    <>
      <div className="relative bg-gradient-to-br from-[#D4AF37] via-[#E5C97A] to-[#A07828] py-12 px-8 text-center overflow-hidden">
        <div className="absolute inset-0 noise-overlay opacity-20 pointer-events-none" />
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 0.8, ease: 'backOut', delay: 0.2 }}
          className="w-24 h-24 rounded-full bg-rolex-green flex items-center justify-center mx-auto mb-5 shadow-2xl border-4 border-cream/30"
        >
          <Crown className="w-12 h-12 text-gold" strokeWidth={2.5} />
        </motion.div>
        <p className="text-[10px] text-rolex-green/80 uppercase tracking-[0.3em] font-bold mb-2">
          {isFR ? '✓ Abonnement actif' : '✓ Subscription active'}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-rolex-green">
          {isFR ? 'Bienvenue VIP !' : 'Welcome to VIP!'}
        </h1>
      </div>

      <div className="px-8 py-8 text-center">
        <p className="text-rolex-green/75 text-lg mb-5">
          {isFR ? 'Votre abonnement' : 'Your subscription'} <strong>{planName}</strong> {isFR ? 'est désormais actif.' : 'is now active.'}
        </p>

        <div className="bg-rolex-green text-cream rounded-2xl p-6 mb-7 border border-gold/30 relative overflow-hidden">
          <div className="absolute inset-0 hero-spotlight opacity-30 pointer-events-none" />
          <p className="relative z-10 text-[10px] uppercase tracking-[0.25em] text-gold font-bold mb-3">{isFR ? 'Avantages VIP' : 'VIP Perks'}</p>
          {ppm > 0 && (
            <p className="relative z-10 font-mono text-3xl font-black text-gold-gradient leading-none mb-2">
              +{ppm.toLocaleString()}
            </p>
          )}
          <p className="relative z-10 text-cream/80 text-sm">
            {isFR ? `points crédités chaque mois` : `points credited monthly`}
          </p>
          <p className="relative z-10 text-cream/55 text-xs mt-3 font-mono">£{Number(amount).toFixed(2)}/{isFR ? 'mois' : 'month'}</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={() => navigate('/account')} className="btn-gold gap-2 px-6 h-12 font-bold tracking-wide">
            <UserIcon className="w-4 h-4" /> {isFR ? 'Voir mon compte' : 'View my account'} <ArrowRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={() => navigate('/')} className="border-rolex-green/30 text-rolex-green hover:bg-rolex-green/5 gap-2 h-12">
            <Home className="w-4 h-4" /> {isFR ? 'Accueil' : 'Home'}
          </Button>
        </div>
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════════════
   GENERIC SUCCESS (fallback)
   ════════════════════════════════════════════════════════ */
function GenericSuccess({ amount, isFR, navigate }) {
  return (
    <div className="p-12 text-center">
      <div className="w-24 h-24 rounded-full bg-gold-gradient flex items-center justify-center mx-auto mb-6 shadow-2xl">
        <CheckCircle2 className="w-12 h-12 text-rolex-green" strokeWidth={2.5} />
      </div>
      <h1 className="font-display text-3xl font-black text-rolex-green mb-3">
        {isFR ? 'Paiement confirmé !' : 'Payment confirmed!'}
      </h1>
      <p className="font-mono text-2xl font-bold text-gold-dark mb-7">£{Number(amount).toFixed(2)}</p>
      <Button onClick={() => navigate('/account')} className="btn-gold gap-2">
        {isFR ? 'Voir mon compte' : 'View my account'} <ArrowRight className="w-4 h-4" />
      </Button>
    </div>
  );
}
